import { Button } from "@itandi/itandi-bb-ui/Button";
import { FloatingMenu } from "@itandi/itandi-bb-ui/FloatingMenu";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ComponentProps } from "react";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import { VendorDealLayout } from "~/components/common/layouts/deals/VendorDealLayout";
import { VendorLayoutV2 } from "~/components/common/layouts/VendorLayoutV2";
import Loading from "~/components/common/parts/loading/Loading";
import { ReportImagesArea } from "~/components/common/reports/ReportImagesArea";
import { ReportImagesAreaSp } from "~/components/common/reports/ReportImagesAreaSp";
import { ReportImageUploadPanel } from "~/components/common/reports/ReportImageUploadPanel";
import { useDealInspectionReportImages } from "~/hooks/vendors/deals/useDealInspectionReportImages";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { NskResponse } from "~/models/Api";
import type {
	DealInspectionReportImage,
	DealReportBulkUpdateParams,
	DealReportImage
} from "~/models/DealReportImage";
import {
	NUMBER_OF_DELETE_AT_ONCE,
	NUMBER_OF_UPLOAD_AT_ONCE
} from "~/models/DealReportImage";
import { isSubVendor } from "~/models/DealVendorCompany";
import { PATH as DEALS_PATH } from "~/pages/vendors/deals";
import { PATH } from "~/pages/vendors/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { getDownloadPath as getDownloadPdfPath } from "~/utils/api/vendors/deals/DealInspectionReport";
import {
	create as inspectionComplete,
	paths as inspectionPaths
} from "~/utils/api/vendors/deals/DealInspectionReportComplete";
import {
	bulkDelete,
	bulkUpdate,
	destroy,
	getDownloadPath as getDownloadImagePath,
	getDownloadZipPath,
	create as postInspectionReportImages,
	update,
	updateSeq
} from "~/utils/api/vendors/deals/DealInspectionReportImage";
import Styles from "./Page.module.scss";

export function Page() {
	const router = useRouter();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});

	const [uploading, setUploading] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [progress, setProgress] = useState(0);
	const [selectedImageIds, setSelectedImageIds] = useState<readonly number[]>(
		[]
	);
	const { data: session } = useSessions().index();
	const { data: deal } = useDeals().show(id);
	// globalMutateしたい
	const { data: reportImages, mutate: mutateReportImages } =
		useDealInspectionReportImages().index(id);

	const { trigger, isMutating } = useSWRMutation(
		inspectionPaths.create(id),
		async (_key, { arg }: { arg: { dealId: number } }) =>
			inspectionComplete(arg.dealId)
	);

	const onComplete = useCallback(async () => {
		const result = await trigger({ dealId: id });
		if (isSuccess(result)) {
			await router.push(PATH(id));
			toast.success("完了しました。");
		} else {
			toast.error(`エラーが発生しました。${result.message ?? ""}`);
		}
	}, [id, trigger, router]);

	if (deal === null || reportImages === null || session === null) {
		return <Loading isLoading />;
	}
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "立会報告画像の登録" }
	] as const satisfies ComponentProps<typeof VendorDealLayout>["items"];
	const handleDrop = async (files: File[]): Promise<void> => {
		setProgress(0);
		setUploading(true);
		const arrFiles: File[][] = [];
		const totalFiles: number = files.length;
		let finishedFiles = 0;
		while (files.length > 0) {
			arrFiles.push(files.splice(0, NUMBER_OF_UPLOAD_AT_ONCE));
		}
		for (const sendFiles of arrFiles) {
			const result = await postInspectionReportImages(sendFiles, deal.id);
			if (isSuccess(result)) {
				await mutateReportImages();
			} else {
				toast.error("エラーが発生しました");
				return;
			}
			finishedFiles += sendFiles.length;
			setProgress(Math.round((finishedFiles / totalFiles) * 100));
			await mutateReportImages();
		}
		setUploading(false);
		toast.success("画像を登録しました");
	};
	const handleUpdate = async (
		submitValue: DealReportImage
	): Promise<NskResponse> => update(submitValue);
	const handleBulkUpdate = async (
		submitValue: DealReportBulkUpdateParams
	): Promise<void> => {
		const result = await bulkUpdate(deal.id, selectedImageIds, submitValue);
		if (isSuccess(result)) {
			await mutateReportImages();
			toast.success("更新しました");
		} else {
			toast.error("エラーが発生しました");
		}
	};
	const handleDestroy = async (
		reportImage: DealReportImage
	): Promise<NskResponse> => {
		const result = await destroy(reportImage);
		if (isSuccess(result)) {
			setSelectedImageIds((prev) => {
				const imageIndex = prev.findIndex(
					(imageId) => imageId === reportImage.id
				);

				return imageIndex === -1 ? prev : prev.toSpliced(imageIndex, 1);
			});
		}
		return result;
	};
	const handleBulkDelete = async (): Promise<void> => {
		if (selectedImageIds.length === 0) {
			return;
		}
		setProgress(0);
		setDeleting(true);
		const imageIdArray: number[][] = [];
		const totalFiles: number = selectedImageIds.length;
		let finishedFiles = 0;
		for (
			let i = 0, j = selectedImageIds.length;
			i < j;
			i += NUMBER_OF_DELETE_AT_ONCE
		) {
			imageIdArray.push(
				selectedImageIds.slice(i, i + NUMBER_OF_DELETE_AT_ONCE)
			);
		}
		for (const ids of imageIdArray) {
			const result = await bulkDelete(deal.id, ids);
			if (isSuccess(result)) {
				await mutateReportImages();
			} else {
				toast.error("エラーが発生しました");
				return;
			}
			finishedFiles += ids.length;
			setProgress(Math.round((finishedFiles / totalFiles) * 100));
			await mutateReportImages();
		}
		setDeleting(false);
		setSelectedImageIds([]);
		toast.success("画像を削除しました");
	};
	const handleUpdateSeq = async (
		newOrderItems: DealReportImage[]
	): Promise<void> => {
		await mutateReportImages(newOrderItems as DealInspectionReportImage[], {
			revalidate: false
		});
		await updateSeq(newOrderItems, id);
	};
	const handleDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, undefined);
		window.open(path);
	};
	const handleDownloadImage = (reportImage: DealReportImage): void => {
		const path = getDownloadImagePath(reportImage);
		window.open(path);
	};
	const handleDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id);
		window.open(path);
	};
	const handleSelectedDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id, selectedImageIds);
		window.open(path);
	};
	const handleSelectedDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, selectedImageIds);
		window.open(path);
	};
	const onChangeSelectedImage = (selectedImageId: number): void => {
		if (selectedImageIds.includes(selectedImageId)) {
			setSelectedImageIds(
				selectedImageIds.filter((imageId) => imageId !== selectedImageId)
			);
		} else {
			setSelectedImageIds(selectedImageIds.concat([selectedImageId]));
		}
	};
	const handleResetSelectedIds = (): void => {
		setSelectedImageIds([]);
	};
	const handleSelectAllImages = (): void => {
		setSelectedImageIds(reportImages.map((image: DealReportImage) => image.id));
	};
	return (
		<VendorLayoutV2>
			<VendorDealLayout deal={deal} enableChat items={items} noBackground>
				<div className={Styles.MainWrapper}>
					<h2 className={Styles.ReportTitle}>立会報告画像の登録</h2>
					<ReportImageUploadPanel
						deleting={deleting}
						handleDrop={handleDrop}
						progress={progress}
						uploading={uploading}
					/>
					<ReportImagesArea
						companyType="vendor"
						handleBulkDelete={handleBulkDelete}
						handleBulkUpdate={handleBulkUpdate}
						handleDestroy={handleDestroy}
						handleDownloadImage={handleDownloadImage}
						handleDownloadPdf={handleDownloadPdf}
						handleDownloadZip={handleDownloadZip}
						handleResetSelectedIds={handleResetSelectedIds}
						handleSelectAllImages={handleSelectAllImages}
						handleSelectedDownloadPdf={handleSelectedDownloadPdf}
						handleSelectedDownloadZip={handleSelectedDownloadZip}
						handleUpdate={handleUpdate}
						handleUpdateSeq={handleUpdateSeq}
						onChangeSelectedImage={onChangeSelectedImage}
						reportImages={reportImages}
						selectedImageIds={selectedImageIds}
					/>
					<ReportImagesAreaSp
						companyType="vendor"
						handleBulkDelete={handleBulkDelete}
						handleBulkUpdate={handleBulkUpdate}
						handleDestroy={handleDestroy}
						handleDownloadImage={handleDownloadImage}
						handleDownloadPdf={handleDownloadPdf}
						handleDownloadZip={handleDownloadZip}
						handleResetSelectedIds={handleResetSelectedIds}
						handleSelectAllImages={handleSelectAllImages}
						handleSelectedDownloadPdf={handleSelectedDownloadPdf}
						handleSelectedDownloadZip={handleSelectedDownloadZip}
						handleUpdate={handleUpdate}
						handleUpdateSeq={handleUpdateSeq}
						onChangeSelectedImage={onChangeSelectedImage}
						reportImages={reportImages}
						selectedImageIds={selectedImageIds}
					/>
				</div>
				{selectedImageIds.length === 0 && (
					<FloatingMenu
						fitToParent
						rightArea={
							<Button
								loading={isMutating}
								onClick={onComplete}
								variant="primary"
							>
								{isSubVendor(deal, session.vendorCompany.id)
									? "完了して通知"
									: "完了して戻る"}
							</Button>
						}
					/>
				)}
			</VendorDealLayout>
		</VendorLayoutV2>
	);
}
